import { keysSelector } from 'Modules/converter/index.js';

export default function makeTemplateApplicator(widget) {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const mapValue = typeMap[widget.type] || {};
  const Component = widget.component;
  let config = { getProps: () => ({}) };

  if (typeof mapValue === 'object') {
    config = {
      ...config,
      ...mapValue,
    };
  }

  const { getProps } = config;

  return Component
    ? (template, { widgetName, items }) => {
        const props = { template, items, ...getProps(widget) };
        return <Component {...props} key={widgetName} />;
      }
    : null;
}

const facetPanelKeys = [
  'fields',
  'name',
  'ignoreFields',
  'showAllAlways',
  'disableCollapse',
  'initCollapsed',
  'initExpandedFacets',
  'initCollapsedFacets',
  'valuesCountToShowFilterInput',
];

const searchBoxKeys = ['name', 'onDropdownItemsReceived', 'onSubmit', 'disableDropdown', 'redirectUrl'];

const vehicleWidgetKeys = [
  'name',
  'columnBreakpoint',
  'doNotRedirectOnVehicleSelect',
  'initCollapsed',
  'isAlwaysColumnLayout',
  'isAutoVehicleSelectionDisabled',
  'isVehicleSelectionIsolated',
  'facetToggleConfig',
  'fields',
  'globalVehicleDiscardEnabled',
  'redirectUrl',
  'useNativeDropdown',
  'useSearchableDropdown',
  'autoOpenDropdowns',
];
const vehicleWidgetKeysSelector = keysSelector(...vehicleWidgetKeys);
const vehicleWidgetTabsKeysSelector = keysSelector(...vehicleWidgetKeys, 'tabs');

const typeMap = {
  DialogButton: {
    getProps: keysSelector('dialogName', 'onClick', 'metadata'),
  },
  FacetPanel: {
    getProps: keysSelector(...facetPanelKeys),
  },
  FacetBar: {
    getProps: keysSelector('fields', 'initCollapsed', 'useNativeDropdown', 'minFacetCount', 'showHitCount'),
  },
  FacetToggle: {
    getProps: keysSelector('toggledFacet', 'visibleIfFields', 'initToggled', 'isInverted', 'saveLastState'),
  },
  FilterChips: {
    getProps: keysSelector('fields', 'ignoreFields'),
  },
  FitmentTable: {
    getProps: keysSelector('initCollapsed', 'columnBreakpoint'),
  },
  Garage: {
    getProps: keysSelector(
      'name',
      'doNotRedirectOnVehicleSelect',
      'isAutoVehicleSelectionDisabled',
      'columnBreakpoint',
      'redirectUrl',
      'useNativeDropdown',
      'useVehicleWidget',
    ),
  },
  SingleVehicleGarage: {
    getProps: keysSelector('withCurrentVehicle'),
  },
  RequestPanel: {
    getProps: keysSelector(
      'isAutoRedirectDisabled',
      'initCollapsed',
      'inputFields',
      'onSubmit',
      'requiredFields',
      'redirectUrl',
      'requestExtra',
      'selectFields',
      'shouldBeIsolated',
      'mayDiscardIsolatedSelection',
      'useNativeDropdown',
      'localStorageKey',
    ),
  },
  ProductData: {
    getProps: keysSelector('fields'),
  },
  RelatedItems: {
    getProps: keysSelector(
      'name',
      'count',
      'getSelection',
      'onItemsRendered',
      'requestExtra',
      'sort',
      'useManualItems',
    ),
  },
  BuyItemsTogether: {
    getProps: keysSelector('name', 'mode', 'count', 'onItemsRendered'),
  },
  SearchBox: {
    getProps: keysSelector(...searchBoxKeys),
  },
  SearchBoxDialogButton: {
    getProps: keysSelector(...searchBoxKeys),
  },
  SearchHeader: {
    getProps: keysSelector(
      'facetToggleConfig',
      'pageSizes',
      'pageSizeSelectClass',
      'doNotHideOnNoResults',
      'sortSelectClass',
    ),
  },
  SearchResult: {
    getProps: keysSelector('instantInfiniteScroll', 'infiniteScrollDisabled', 'updateCallback'),
  },
  VehicleWidget: {
    getProps: (widget) => {
      const [nameRoot] = widget.name.split('_');
      return {
        ...vehicleWidgetKeysSelector(widget),
        isAlwaysActive:
          widget.isAlwaysActive ??
          (nameRoot === 'HeaderVehicleWidget' ||
            nameRoot === 'HomeVehicleWidget' ||
            nameRoot === 'VehicleSpecs'),
      };
    },
  },
  VehicleTabs: {
    getProps: (widget) => ({
      ...vehicleWidgetTabsKeysSelector(widget),
    }),
  },
  VehicleTiles: {
    getProps: keysSelector('name', 'doNotRedirectOnVehicleSelect'),
  },
  VerifyFitment: {
    getProps: keysSelector(
      'name',
      'initCollapsed',
      'isAlwaysColumnLayout',
      'isAutoVehicleSelectionDisabled',
      'globalVehicleDiscardEnabled',
      'columnBreakpoint',
      'useNativeDropdown',
      'useSearchableDropdown',
    ),
  },
  FacetTiles: {
    getProps: keysSelector(
      'columnBreakpoint',
      'excludeFieldPreselection',
      'facetField',
      'initCollapsed',
      'isAlwaysActive',
      'isMultiLevel',
      'isVehicleWidgetDisabled',
      'useFilterMode',
      'view',
      'useNativeDropdown',
      'useSearchableDropdown',
    ),
  },
  SearchBoxDialog: {
    getProps: keysSelector(...searchBoxKeys),
  },
  FacetDialog: {
    getProps: keysSelector(...facetPanelKeys),
  },
  VehicleWidgetDialog: {
    getProps: keysSelector(
      'columnBreakpoint',
      'doNotRedirectOnVehicleSelect',
      'isAutoVehicleSelectionDisabled',
      'isAlwaysColumnLayout',
      'fields',
      'globalVehicleDiscardEnabled',
      'redirectUrl',
      'useNativeDropdown',
      'useSearchableDropdown',
    ),
  },
  SimpleColorPaletteDialog: {
    getProps: keysSelector('field'),
  },
  ColorExtractorDialog: {
    getProps: keysSelector('field'),
  },
  VehicleInfo: {
    getProps: keysSelector('initCollapsed', 'columnBreakpoint'),
  },
  WheelTireVehicleInfo: {
    getProps: keysSelector('initCollapsed', 'autoSyncVisualization'),
  },
  AutoSyncVisualization: {
    getProps: keysSelector('apiKey', 'config', 'classModificator'),
  },
  AutoSyncVisualizationDialog: {
    getProps: keysSelector('apiKey', 'config'),
  },
};
